import { useEffect, useState } from 'react';
import { SignIn } from '@clerk/clerk-react';
import { signInAppearance } from '../theme/clerkAppearance';

type LogStep = {
    t: string;
    kind: 'alert' | 'investigate' | 'rca' | 'pr' | 'verify';
    text: string;
    meta?: string;
    chip: string;
    dot: string;
    label: string;
};

const SCRIPT: LogStep[] = [
    {
        t: '00.00',
        kind: 'alert',
        text: 'PagerDuty · checkout-api p99 latency 6.2s (SLO 1.5s)',
        meta: 'P1',
        chip: 'alert',
        dot: 'red',
        label: 'ALERT'
    },
    {
        t: '00.04',
        kind: 'investigate',
        text: 'Pulled 14m of logs · 9 services · 412 traces',
        meta: 'context',
        chip: 'investigate',
        dot: 'cyan',
        label: 'INVESTIGATE'
    },
    {
        t: '00.11',
        kind: 'investigate',
        text: 'Hypothesis 1/4 · DB connection pool exhausted — confirmed',
        meta: 'evidence',
        chip: 'investigate',
        dot: 'cyan',
        label: 'INVESTIGATE'
    },
    {
        t: '00.18',
        kind: 'rca',
        text: 'Root cause · idle connection leak in `payments-worker@4.12.1`',
        meta: 'RCA',
        chip: 'rca',
        dot: 'yellow',
        label: 'RCA'
    },
    {
        t: '00.22',
        kind: 'pr',
        text: 'Opened PR #4831 · close conn on retry-after-error path',
        meta: '+12 / -3',
        chip: 'pr',
        dot: 'green',
        label: 'PULL REQUEST'
    },
    {
        t: '00.24',
        kind: 'verify',
        text: 'CI green · canary holds 320ms p99 · rollout proposed',
        meta: 'ready to merge',
        chip: 'verify',
        dot: 'green',
        label: 'VERIFIED'
    }
];

function LiveAgentTerminal() {
    const [shown, setShown] = useState(1);
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        if (resetting) {
            const timer = setTimeout(() => {
                setShown(1);
                setResetting(false);
            }, 600);
            return () => clearTimeout(timer);
        }

        if (shown >= SCRIPT.length) {
            const timer = setTimeout(() => {
                setResetting(true);
            }, 5000);
            return () => clearTimeout(timer);
        }

        const delays = [1200, 1500, 1500, 1400, 1200, 1500];
        const timer = setTimeout(
            () => {
                setShown((prev) => prev + 1);
            },
            delays[shown - 1] || 1200
        );

        return () => clearTimeout(timer);
    }, [shown, resetting]);

    const visibleSteps = resetting ? [] : SCRIPT.slice(0, shown);
    const showCursor = !resetting && shown < SCRIPT.length;
    const showClosed = !resetting && shown >= SCRIPT.length;

    return (
        <div className={`signin-terminal ${resetting ? 'resetting' : ''}`}>
            {/* terminal chrome */}
            <div className="signin-terminal-chrome">
                <div className="signin-terminal-dots">
                    <span className="signin-terminal-dot red" />
                    <span className="signin-terminal-dot yellow" />
                    <span className="signin-terminal-dot green" />
                </div>
                <div className="signin-terminal-status">
                    <span className="signin-terminal-pulse" />
                    selfheal-agent · on shift
                </div>
                <div className="signin-terminal-id">incident-9f3a</div>
            </div>

            {/* scan line */}
            <div className="signin-scanline" />

            {/* terminal logs */}
            <div className="signin-terminal-body">
                <div className="signin-terminal-logs">
                    {visibleSteps.map((step, idx) => (
                        <div key={idx} className="signin-terminal-row">
                            <span className="signin-terminal-ts">{step.t}</span>
                            <span
                                className={`signin-terminal-chip ${step.chip}`}
                            >
                                <span className={step.dot} />
                                {step.label}
                            </span>
                            <span className="signin-terminal-text pr-1">
                                {step.text}
                            </span>
                            {step.meta && (
                                <span className="signin-terminal-meta">
                                    {step.meta}
                                </span>
                            )}
                        </div>
                    ))}

                    {showCursor && (
                        <div className="signin-terminal-cursor">
                            <span>▍</span>
                            <span className="blink">_</span>
                            <span className="pulse-text">listening…</span>
                        </div>
                    )}
                </div>

                {/* PR Code Block / Incident closed status */}
                <div
                    className="signin-terminal-pr-box"
                    style={{ display: showClosed ? 'flex' : 'none' }}
                >
                    {showClosed && (
                        <>
                            {/* Badge */}
                            <div className="signin-terminal-pr-badge">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                Incident closed · MTTR 24s · 1 PR ready for
                                review
                            </div>

                            {/* Commit Details code block */}
                            <pre className="signin-terminal-pr-pre">
                                {`fix(payments-worker): close DB connection on retry-after-error path

incident:    incident-9f3a · checkout-api p99 6.2s (SLO 1.5s)
root cause:  pool exhausted — connections leaked when a retry was
             scheduled before close() ran. confirmed against
             40m of cloudtrail + pg_stat_activity diff.

evidence:    https://healops.ai/i/9f3a   (auditable trail)
regression:  added e2e test that reproduces the leak in <2s.

verified:    canary held 320ms p99 for 12m. ready to merge.

Co-authored-by: selfheal-agent <agent@healops.ai>`}
                            </pre>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function SignInPage() {
    return (
        <div className="signin-split-container">
            {/* Left panel (Clerk Authentication) */}
            <div className="signin-left-panel">
                <div className="signin-logo-header">
                    <img
                        className="signin-logo-img"
                        src="/logo.webp"
                        alt="SelfHeal"
                    />
                    <span className="signin-logo-text">SelfHeal</span>
                </div>

                <div>
                    <SignIn
                        routing="path"
                        path="/sign-in"
                        fallbackRedirectUrl="/investigations"
                        appearance={signInAppearance}
                    />
                </div>
            </div>

            {/* Right panel (SelfHeal showcase) */}
            <div className="signin-right-panel">
                {/* grid background */}
                <div className="signin-grid-bg" />
                {/* radial wash */}
                <div className="signin-radial-wash" />

                {/* Top visual elements */}
                <div
                    style={{
                        position: 'relative',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px'
                    }}
                >
                    <div className="signin-beta-pill">
                        <span className="signin-beta-dot" />
                        The agentic SRE — now in private beta
                    </div>

                    <h1 className="signin-showcase-heading">
                        The autonomous SRE that doesn't just{' '}
                        <span className="signin-showcase-heading dimmed">
                            find the bug.
                        </span>{' '}
                        <span className="shimmer-text">It ships the fix.</span>
                    </h1>

                    <p className="signin-showcase-lead">
                        SelfHeal investigates the moment an alert fires —
                        correlates signals across logs, traces and configs,
                        tests hypotheses in parallel, isolates root cause, and
                        opens a reviewed pull request with a regression test
                        attached.
                    </p>
                </div>

                {/* Console / Terminal Simulator */}
                <div
                    style={{
                        position: 'relative',
                        zIndex: 10,
                        margin: '32px 0',
                        display: 'flex',
                        justifyContent: 'flex-start'
                    }}
                >
                    <LiveAgentTerminal />
                </div>

                {/* Bottom stats/features bar */}
                <div className="signin-value-props">
                    <span className="signin-prop-item">
                        <span className="signin-prop-dot heal" />
                        MTTR · minutes → seconds
                    </span>
                    <span className="signin-prop-item">
                        <span className="signin-prop-dot signal" />
                        Read-only access
                    </span>
                    <span className="signin-prop-item">
                        <span className="signin-prop-dot warn" />
                        Human-in-the-loop merge
                    </span>
                </div>
            </div>
        </div>
    );
}
